<template>
    <InfoDataTextRecurring
        v-if="globalLoading"
        height="50vh"
        :value="'loading'"
    />
    <no-connected-numbers
        v-else-if="!hasAnyNumbers && !hasNumbers && !hasTiktokAccounts"
        class="integration-page"
    />
    <div class="integration-page" v-else>
        <div class="integration-data">
            <CounterBlock
                v-for="el in baseStats"
                :key="el.key"
                :icon="el.icon"
                :count="el.count"
                :info="`page.integration.integration_data.${el.key}`"
            />
        </div>
        <section class="number-add">
            <h1 class="title">{{ $t(`page.integration.numbers_table.title${titleSuffix}`) }}:</h1>
            <div class="connected-numbers">
                <!-- VISIBLE NUMBERS -->
                <AccordionRecurring
                    :title="$t(`page.integration.numbers_table.visible_title${titleSuffix}`)"
                    :isOpen="true"
                >
                    <div class="border_padding">
                        <AllNumbersTablesIntegration
                            type="visible"
                            :data="visibleList"
                            @onSearchValue="
                (searchPayload) =>
                  getNumberDataBySearchValue({
                    ...searchPayload,
                    visibleNumber: 'visible',
                  })
              "
                        />
                        <template v-if="hasEarlyAccess">
                            <hr class="mb-4"/>
                            <AllTiktokAccountsTable
                                type="visible"
                                :account-list="visibleTiktokAccounts"
                            />
                        </template>
                    </div>
                </AccordionRecurring>

                <!-- INVISIBLE NUMBERS -->
                <template
                    v-if="
                        !!Object.values(invisibleList).flat().length ||
                        !!invisibleTiktokAccounts.length
                    "
                >
                    <AccordionRecurring
                        :title="$t(`page.integration.numbers_table.invisible_title${titleSuffix}`)"
                    >
                        <div class="border_padding">
                            <AllNumbersTablesIntegration
                                type="invisible"
                                :data="invisibleList"
                            />
                            <template v-if="!!invisibleTiktokAccounts.length">
                                <hr class="mb-4"/>
                                <AllTiktokAccountsTable
                                    type="invisible"
                                    :account-list="invisibleTiktokAccounts"
                                />
                            </template>
                        </div>
                    </AccordionRecurring>
                </template>
            </div>
        </section>
    </div>
</template>
<script setup>
// core
import {reactive, computed, watch, onMounted} from "vue";
import {useStore} from "vuex";
import {isCookieSet, setCookie} from "@/helpers/cookies";

// components
import AccordionRecurring from "@/components/Recurring/AccordionRecurring.vue";
import CounterBlock from "@/components/Integration/CounterBlock.vue";
import AllNumbersTablesIntegration from "@/components/Integration/AllNumbersTablesIntegration.vue";
import AllTiktokAccountsTable from "@/components/Tiktok/Table/AllTiktokAccountsTable.vue";
import NoConnectedNumbers from "@/components/Integration/NoConnectedNumbers.vue";
import useIntegration from "@/components/Integration/api/useIntegration";
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue";

const {loadAllCounts, baseStats} = useIntegration();
const store = useStore();

const globalLoading = computed(() => {
    return !store.state.user.userData
});
const allTableNumbersData = reactive({
    visible: {
        viber: [],
        telegram: [],
        whatsapp: [],
    },
    invisible: {
        viber: [],
        telegram: [],
        whatsapp: [],
    },
});

watch(
    () => store.getters["numbers/getVisibleNumbersViber"],
    () => {
        allTableNumbersData.visible.viber =
            store.getters["numbers/getVisibleNumbersViber"];
    }
);
watch(
    () => store.getters["numbers/getVisibleNumbersTelegram"],
    () => {
        allTableNumbersData.visible.telegram =
            store.getters["numbers/getVisibleNumbersTelegram"];
    }
);
watch(
    () => store.getters["numbers/getVisibleNumbersWhatsapp"],
    () => {
        allTableNumbersData.visible.whatsapp =
            store.getters["numbers/getVisibleNumbersWhatsapp"];
    }
);
watch(
    () => store.getters["numbers/getInvisibleNumbersViber"],
    () => {
        allTableNumbersData.invisible.viber =
            store.getters["numbers/getInvisibleNumbersViber"];
    }
);
watch(
    () => store.getters["numbers/getInvisibleNumbersTelegram"],
    () => {
        allTableNumbersData.invisible.telegram =
            store.getters["numbers/getInvisibleNumbersTelegram"];
    }
);
watch(
    () => store.getters["numbers/getInvisibleNumbersWhatsapp"],
    () => {
        allTableNumbersData.invisible.whatsapp =
            store.getters["numbers/getInvisibleNumbersWhatsapp"];
    }
);

// TikTok is open to the early access accounts only: the rest see numbers alone
const hasEarlyAccess = computed(() => store.getters["auth/hasEarlyAccess"]);
const titleSuffix = computed(() => hasEarlyAccess.value ? "_with_accounts" : "");

const visibleTiktokAccounts = computed(
    () => hasEarlyAccess.value ? store.getters["tiktok/getVisibleAccounts"] ?? [] : []
);
const invisibleTiktokAccounts = computed(
    () => hasEarlyAccess.value ? store.getters["tiktok/getInvisibleAccounts"] ?? [] : []
);

const hasTiktokAccounts = computed(
    () => !!visibleTiktokAccounts.value.length || !!invisibleTiktokAccounts.value.length
);

const hasAnyNumbers = computed(() => store.getters['user/isUserHasNumbers'])

const hasNumbers = computed(() => {
    const visibleList = Object.values(allTableNumbersData.visible).flat();
    const invisibleList = Object.values(allTableNumbersData.invisible).flat();
    return visibleList.length > 0 || invisibleList > 0;
});

// FOR FILTRATION BY VISIBILITY LIST
const visibleList = computed(() => ({
    viber: allTableNumbersData.visible.viber,
    telegram: allTableNumbersData.visible.telegram,
    whatsapp: allTableNumbersData.visible.whatsapp,
}));
const invisibleList = computed(() => ({
    viber: allTableNumbersData.invisible.viber,
    telegram: allTableNumbersData.invisible.telegram,
    whatsapp: allTableNumbersData.invisible.whatsapp,
}));

const getNumberDataBySearchValue = (inputPayload) => {
    const {input, msgr: messenger, visibleNumber} = inputPayload;
    const visibility = visibleNumber === "visible";

    store.dispatch("numbers/setSearchInput", {input, messenger, visibility});

    store.dispatch("numbers/loadNumbersByMessenger", {
        number: input,
        messenger,
        visibility,
    }); // setSearchInput;
};

watch(() => store.state.user.userData, () => {
    // only right after a sign-in and never over a dialog the user opened themselves
    if (!sessionStorage.getItem("showAnnouncement") || store.state.isDialogOpen) return;

    sessionStorage.removeItem("showAnnouncement");
    // every slide shown now is about telephony or TikTok
    if (!hasEarlyAccess.value) return;

    store.commit("openDialog", "AnnouncementDialog");
})

//   LOAD: VISIBLE & INVISIBLE NUMBERS
const loadNumbersData = async () => {
    const promises = [
        loadAllCounts(),
        store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "viber",
            visibility: true,
        }),
        store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "telegram",
            visibility: true,
        }),
        store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "whatsapp",
            visibility: true,
        }),
        store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "viber",
            visibility: false,
        }),
        store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "telegram",
            visibility: false,
        }),
        store.dispatch("numbers/loadNumbersByMessenger", {
            messenger: "whatsapp",
            visibility: false,
        }),
    ];

    if (hasEarlyAccess.value) {
        promises.push(
            store.dispatch("tiktok/loadAccounts", {visibility: true}),
            store.dispatch("tiktok/loadAccounts", {visibility: false}),
        );
    }

    await Promise.allSettled(promises);
};

onMounted(() => {
    loadNumbersData();
});
</script>

<style lang="scss" scoped>
.integration-page {
    padding: 0 20px 25px 20px;
}

.integration-data {
    display: flex;
    align-items: stretch;
    gap: 17px;
}

.number-add {
    margin-top: 40px;

    .title {
        font-size: 24px;
        font-family: Gilroy;
        font-weight: 500;

        text-transform: uppercase;

        color: #404040ff;
    }

    .connected-numbers {
        margin-top: 16px;
        width: 100%;
    }
}

@media screen and (max-width: 1200px) {
    .integration-data {
        flex-wrap: wrap;

        &:deep(.integration-element) {
            flex: 1 0 calc(50% - 59px);

            .right-element {
                flex: 1;
            }
        }
    }
}

@media screen and (max-width: 1024px) {
    .integration-data {
        &:deep(.integration-element) {
            .right-element {
                flex: 1;
            }
        }
    }

    .integration-page.is-loading {
        height: 200px;
    }
}
</style>
